import { apiClient, type PaginatedResponse } from '../../../api/client'
import buildApiUrl, { buildPaginationParams, type PaginationOptions } from '../../../api/pagination'
import { type PaginatedQueryParameters, type GenericFilterType } from '../../../api/hooks/utils/types'
import { type EntitySearchResponse } from '../../../hooks/useEntitySearch'
import { AccountsusersApi } from '../../../generated-types/apis/AccountsusersApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { getPKfromSelf } from '../../../utils/functions'
import { createUser, createRole, inputToAPIAllInOneUserJSON, roleToUrlString } from '../utils/functions'
import { type User, type Role } from '../../../utils/types'
import { APIAllInOneUser } from '../../../generated-types/models/APIAllInOneUser'

const log = new LogTool({ context: 'EmployeeAPI', enable: true, logLevel: 'warn' })

/**
 * Filter types for User queries
 */
export interface UserFilters extends GenericFilterType {
  username?: string
  email?: string
  first_name?: string
  last_name?: string
  is_active?: string
  is_staff?: string
  organization?: string
  created_at?: string
}

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Create API instance
 */
function getUsersApi(): AccountsusersApi {
  return new AccountsusersApi(getApiConfiguration())
}

/**
 * Fetch paginated users from API
 */
export async function fetchUsers(
  options: PaginatedQueryParameters<User, UserFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<User>> {
  log.info('Fetching users with options:', options)

  const url = buildApiUrl('v1/accounts/users/', options as PaginatedQueryParameters<User, UserFilters>, ids)
  const response = await apiClient.get<PaginatedResponse<any>>(url)

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: (response.results || []).map(createUser),
  }
}

/**
 * Search users using Elasticsearch
 */
export async function searchUsers(
  options: PaginatedQueryParameters<User, UserFilters> = {}
): Promise<EntitySearchResponse<User>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''
  
  const searchFilters = {
    ...filters,
    ...(hasFilters && !hasQ ? { q: '*' } : {}),
  }
  
  const url = buildApiUrl(
    'v1/search/accounts/users/',
    { filters: searchFilters }
  )
  return apiClient.get<EntitySearchResponse<User>>(url)
}

/**
 * Fetch the current logged-in user
 */
export async function fetchSelfUser(expand?: string[]): Promise<User> {
  log.info('Fetching self user')

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 
    ? `v1/accounts/users/self/?${params.join('&')}` 
    : 'v1/accounts/users/self/'

  const response = await apiClient.get<any>(fetchUrl)
  return createUser(response)
}

/**
 * Fetch a single user by ID or URL
 */
export async function fetchUser(
  idOrUrl: string | number,
  expand?: string[]
): Promise<User> {
  log.info('Fetching user:', idOrUrl)

  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `v1/accounts/users/${idOrUrl}/`
    : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createUser(response)
}

/**
 * Create a new user
 * Note: Generated API returns void, so we fall back to apiClient to get the created user
 */
export async function createUserApi(userData: any): Promise<User> {
  log.info('Creating user:', userData)

  const json = inputToAPIAllInOneUserJSON(userData, { includeRoles: true })
  const response = await apiClient.post<any>('v1/accounts/users/', json)

  // Fetch the created user with expanded roles if available
  if (response.self) {
    return fetchUser(response.self, ['roles__sub_roles'])
  }

  return createUser(response)
}

/**
 * Update an existing user
 */
export async function updateUserApi(
  idOrSelf: string | number,
  userData: any
): Promise<User> {
  log.info('Updating user:', idOrSelf, userData)

  const json = inputToAPIAllInOneUserJSON(userData)

  // For users, ID is a string
  if (typeof idOrSelf === 'string' && !idOrSelf.includes('/')) {
    const response = await getUsersApi().v1AccountsUsersUpdate({
      id: idOrSelf,
      aPIAllInOneUserRequest: json,
    })

    // Fetch the updated user with expanded roles
    return fetchUser(response.self, ['roles__sub_roles'])
  }

  // For URL-based updates, fall back to apiClient
  const url = typeof idOrSelf === 'string' && idOrSelf.includes('/') 
    ? idOrSelf 
    : `v1/accounts/users/${idOrSelf}/`
  const response = await apiClient.put<any>(url, json)

  // Fetch the updated user with expanded roles
  return fetchUser(response.self, ['roles__sub_roles'])
}

/**
 * Patch an existing user (partial update)
 */
export async function patchUserApi(
  idOrSelf: string | number,
  userData: Partial<any>
): Promise<User> {
  log.info('Patching user:', idOrSelf, userData)

  const json = inputToAPIAllInOneUserJSON(userData)

  // For users, ID is a string
  if (typeof idOrSelf === 'string' && !idOrSelf.includes('/')) {
    const response = await getUsersApi().v1AccountsUsersPartialUpdate({
      id: idOrSelf,
      aPIPatchedAllInOneUserRequest: json,
    })
    return createUser(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = typeof idOrSelf === 'string' && idOrSelf.includes('/') 
    ? idOrSelf 
    : `v1/accounts/users/${idOrSelf}/`
  const response = await apiClient.patch<any>(url, json)
  return createUser(response)
}

/**
 * Delete a user
 */
export async function deleteUserApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting user:', idOrSelf)

  // For users, ID is a string
  if (typeof idOrSelf === 'string' && !idOrSelf.includes('/')) {
    await getUsersApi().v1AccountsUsersDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = typeof idOrSelf === 'string' && idOrSelf.includes('/') 
    ? idOrSelf 
    : `v1/accounts/users/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Delete multiple users
 */
export async function deleteUsersApi(users: User[]): Promise<void> {
  log.info('Deleting users:', users.length)

  const deletePromises = users.map(user => deleteUserApi(user.self))
  await Promise.all(deletePromises)
}

/**
 * Update user roles
 */
export async function updateUserRolesApi(
  userIdOrSelf: string | number,
  roles: any[]
): Promise<void> {
  log.info('Updating user roles:', userIdOrSelf)

  const userId = typeof userIdOrSelf === 'string' && userIdOrSelf.includes('/')
    ? getPKfromSelf(userIdOrSelf)
    : userIdOrSelf

  const json = {
    roles: roles.map(roleToUrlString),
  }

  await apiClient.put(`v1/accounts/users/${userId}/roles/`, json)
}

/**
 * Fetch user roles
 */
export async function fetchUserRoles(
  userIdOrSelf: string | number
): Promise<Role[]> {
  log.info('Fetching user roles:', userIdOrSelf)

  const userId = typeof userIdOrSelf === 'string' && userIdOrSelf.includes('/')
    ? getPKfromSelf(userIdOrSelf)
    : userIdOrSelf

  const response = await apiClient.get<PaginatedResponse<any>>(`v1/accounts/users/${userId}/roles/`)
  return response.results.map((r: any) => createRole(r))
}

/**
 * Fetch current organization
 */
export async function fetchCurrentOrganization(): Promise<string> {
  log.info('Fetching current organization')

  const response = await apiClient.get<any>('v1/accounts/users/self/')
  return response.organization
}
